/**
 * The one job-polling loop for the whole app (JobProgress, WizardPage, …).
 *
 * Contract:
 *  - polls GET /api/jobs/:id starting at the server-declared interval;
 *  - STOPS as soon as the job reaches a terminal state (driven by the
 *    shared state map, not a hardcoded ["COMPLETED","FAILED"] pair);
 *  - backs off (2s → 5s → 10s cap) after repeated unchanged polls or errors;
 *  - pauses while the tab is hidden (react-query focus manager);
 *  - aborts the in-flight request on unmount (AbortController via
 *    react-query's queryFn signal);
 *  - fires a toast + optional callback when the job transitions to a
 *    terminal state under our eyes.
 */
import { useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api, type JobState } from "../lib/api";
import { categoryOf, isTerminal, pollIntervalMs } from "../lib/states";
import { POLL_BACKOFF_AFTER, POLL_BACKOFF_STEPS_MS } from "../lib/config";
import { useToast } from "./useToast";

export type JobPollingOptions = {
  onTerminal?: (job: JobState) => void;
  /** Fire a toast when the job transitions to terminal (default true). */
  toastOnTerminal?: boolean;
};

/** Pick the backoff step for a number of consecutive quiet/failed polls. */
export function backoffStepMs(baseMs: number, strikes: number): number {
  const index = Math.min(
    Math.floor(strikes / POLL_BACKOFF_AFTER),
    POLL_BACKOFF_STEPS_MS.length - 1,
  );
  return Math.max(baseMs, POLL_BACKOFF_STEPS_MS[index]);
}

export function useJobPolling(
  jobId: string | null | undefined,
  options: JobPollingOptions = {},
) {
  const { t } = useTranslation();
  const toast = useToast();
  const { onTerminal, toastOnTerminal = true } = options;

  // Latest callback without re-subscribing the effect.
  const onTerminalRef = useRef(onTerminal);
  onTerminalRef.current = onTerminal;

  // Consecutive polls with an unchanged payload → backoff driver.
  const quietRef = useRef({ jobId: "", lastJson: "", unchanged: 0 });
  const prevStateRef = useRef<string | null>(null);

  const query = useQuery<JobState>({
    queryKey: ["job", jobId],
    queryFn: async ({ signal }) => {
      const job = await api.job(jobId as string, signal);
      const tracker = quietRef.current;
      const json = JSON.stringify(job);
      if (tracker.jobId !== jobId) {
        quietRef.current = { jobId: jobId as string, lastJson: json, unchanged: 0 };
      } else if (tracker.lastJson === json) {
        tracker.unchanged += 1;
      } else {
        tracker.lastJson = json;
        tracker.unchanged = 0;
      }
      return job;
    },
    enabled: Boolean(jobId),
    retry: false,
    staleTime: 0,
    refetchInterval: (q) => {
      const job = q.state.data;
      if (job && isTerminal(job.state)) return false;
      const strikes = Math.max(
        quietRef.current.jobId === jobId ? quietRef.current.unchanged : 0,
        q.state.fetchFailureCount,
      );
      return backoffStepMs(pollIntervalMs(), strikes);
    },
    refetchIntervalInBackground: false,
  });

  const job = query.data ?? null;

  // Terminal-transition side effects (toast + callback), once per job.
  useEffect(() => {
    if (!job) return;
    const prev = prevStateRef.current;
    prevStateRef.current = job.state;
    if (prev === null || prev === job.state) return;
    if (isTerminal(prev) || !isTerminal(job.state)) return;
    onTerminalRef.current?.(job);
    if (toastOnTerminal) {
      const category = categoryOf(job.state);
      if (category === "success") {
        toast(t("toasts.jobCompleted"), "success");
      } else if (category === "warning") {
        toast(t("toasts.jobCompletedControls"), "warning");
      } else {
        toast(t("toasts.jobFailed"), "error");
      }
    }
  }, [job, t, toast, toastOnTerminal]);

  // Reset trackers when the observed job changes.
  useEffect(() => {
    prevStateRef.current = null;
  }, [jobId]);

  return {
    job,
    error: query.error,
    isLoading: Boolean(jobId) && query.isPending,
    refetch: query.refetch,
    terminal: job ? isTerminal(job.state) : false,
  };
}
